<template>
  <div class="adobe-pdf-viewer">
    <div v-show="isPdf" :id="divId" class="adobe-pdf-viewer__container"></div>
    <div v-if="isLoading" class="adobe-pdf-viewer__spinner">
      <SpinnerWrapper :size="SpinnerSize.L" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, ref, watch } from 'vue';
  import { useStore } from 'vuex';
  import axios, { AxiosResponse, CancelTokenSource } from 'axios';
  import { getFileNameFromContentDisposition } from 'utils';
  import { AdobeDC, DefaultViewMode } from '@/interfaces/adobe';
  import { IVueexState } from '@/store';
  import SpinnerWrapper from '@/components/SpinnerWrapper/SpinnerWrapper.vue';
  import { SpinnerSize } from '@/components/SpinnerWrapper/SpinnerWrapper.types';

  const props = defineProps<{
    fileEndpoint: string;
    divId?: string;
  }>();

  const emit = defineEmits<{
    (e: 'loaded', filename: string | undefined, isPdf: boolean, fileUrl: string): void;
    (e: 'error'): void;
  }>();

  const divId = props.divId ?? 'adobe-dc-view';

  const store = useStore<IVueexState>();

  const isPdf = ref(false);
  const isLoading = ref(false);
  const cancelTokenSource = ref<CancelTokenSource>(axios.CancelToken.source());

  async function init(): Promise<void> {
    isLoading.value = true;

    const adobeScriptUrl = store.state.adobeScriptUrl;
    const adobeClientId = store.state.adobeClientId;

    if (!document.getElementById('adobe-sdk-script')) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.id = 'adobe-sdk-script';
        script.src = adobeScriptUrl;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Adobe SDK script'));
        document.head.appendChild(script);
      });
    }

    await new Promise<void>((resolve) => {
      const win = window as unknown as AdobeDC;
      if (win.AdobeDC) {
        resolve();
      } else {
        document.addEventListener('adobe_dc_view_sdk.ready', () => resolve(), { once: true });
      }
    });

    const win = window as unknown as AdobeDC;
    if (!win.AdobeDC) {
      emit('error');
      return;
    }

    const adobeDCViewInstance = new win.AdobeDC.View({
      clientId: adobeClientId,
      divId: divId,
    });

    let response: AxiosResponse<Blob>;
    try {
      response = await axios.get(props.fileEndpoint, {
        responseType: 'blob',
        cancelToken: cancelTokenSource.value.token,
      });
    } catch {
      isLoading.value = false;
      emit('error');
      return;
    }

    const filename =
      getFileNameFromContentDisposition(response.headers['content-disposition']) ?? undefined;

    isPdf.value = checkIsPdf(
      (response.headers['content-type'] as string) || undefined,
      filename,
    );

    const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(pdfBlob);

    isLoading.value = false;

    emit('loaded', filename, isPdf.value, blobUrl);

    adobeDCViewInstance.previewFile(
      {
        content: { location: { url: blobUrl } },
        metaData: { fileName: filename || 'file.pdf' },
      },
      {
        showDownloadPDF: false,
        showPrintPDF: false,
        enableFormFilling: false,
        enableAnnotationAPIs: false,
        showAnnotationTools: true,
        defaultViewMode: DefaultViewMode.FIT_WIDTH,
      },
    );
  }

  function checkIsPdf(contentType: string | undefined, fileName: string | undefined): boolean {
    if (contentType?.split(';')[0]?.trim() === 'application/pdf') return true;
    return fileName?.toLocaleLowerCase().endsWith('.pdf') ?? false;
  }

  watch(
    () => store.state.env,
    async (newVal) => {
      if (newVal) {
        store.dispatch('setAdobeClientId', newVal);
        await init();
      }
    },
    { immediate: true, deep: true },
  );

  onBeforeUnmount(() => {
    cancelTokenSource.value.cancel('Component destroyed');
  });

  defineExpose({ init });
</script>

<style scoped lang="scss">
  .adobe-pdf-viewer {
    width: 100%;
    height: 100%;
    position: relative;

    &__container {
      width: 100%;
      height: 100%;
    }

    &__spinner {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
    }
  }
</style>
